import React, { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import useLoader from "../../../../hooks/useLoader";
import useForm from "../../../../hooks/useForm";
import { eventJobFields } from "../../../../constants/forms/eventJobFields";
import Form from "../../../../components/forms/Form";
import useBaseService from "../../../../hooks/services/useBaseService";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const CreateEventJobContractPage = () => {
  const navigate = useNavigate();
  const { contractId, eventId, additiveId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(eventJobFields)
  );
  const { post: create, formErrors } = useBaseService(navigate);

  const queryClient = useQueryClient();

  const createEventJobMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await create(
        entities.contracts.eventAdditiveJobCreate(
          contractId,
          eventId,
          additiveId
        ),
        formData
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        resetForm();
        queryClient.invalidateQueries([
          "contractEventJobs",
          contractId,
          eventId,
        ]);
        navigate(
          `/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`
        );
      }
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      showLoader();
      return createEventJobMutation.mutateAsync(formData);
    } catch (error) {
      console.error("Erro ao enviar serviço de evento:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [createEventJobMutation, formData, showLoader]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`);
  }, [contractId, eventId, navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Serviço de Evento"
        showBackButton={true}
        backUrl={`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`}
      />

      <div className="container-fluid p-1">
        <Form
          handleBack={handleBack}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          initialFormData={formData}
          onSubmit={handleSubmit}
        >
          {() => {
            const renderedSections = new Set();

            return eventJobFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateEventJobContractPage;
